export type CorrectedTextToken =
    | { readonly type: "text"; readonly text: string }
    | { readonly type: "replacement"; readonly wrong: string; readonly correct: string }
    | { readonly type: "deletion"; readonly wrong: string }
    | { readonly type: "insertion"; readonly correct: string };

const normalizeEditLookup = (value: string): string =>
    value.normalize("NFC").toLowerCase().replace(/\s+/g, " ").trim();

const normalizeEditText = (value: string): string =>
    value.normalize("NFC").replace(/\s+/g, " ").trim();

const isBoundary = (value: string, index: number): boolean => {
    if (index <= 0 || index >= value.length) return true;
    return !(/[\p{L}\p{N}]/u.test(value[index - 1]) && /[\p{L}\p{N}]/u.test(value[index]));
};

const startsWithStandaloneTerm = (value: string, term: string): boolean => {
    const haystack = normalizeEditLookup(value);
    const needle = normalizeEditLookup(term);
    return Boolean(needle) && haystack.startsWith(needle) && isBoundary(haystack, needle.length);
};

const endsWithStandaloneTerm = (value: string, term: string): boolean => {
    const haystack = normalizeEditLookup(value);
    const needle = normalizeEditLookup(term);
    return Boolean(needle) &&
        haystack.endsWith(needle) &&
        isBoundary(haystack, haystack.length - needle.length);
};

const plainTextOverlapsWrong = (prevText: string, wrong: string): boolean => {
    const prev = normalizeEditLookup(prevText).replace(/\s+$/, "");
    const needle = normalizeEditLookup(wrong);
    return Boolean(prev) && needle.startsWith(prev);
};

const stripOverlappingPrefix = (prevText: string, value: string): string => {
    const prefix = prevText.replace(/\s+$/, "");
    if (!prefix || value.length < prefix.length) return value;
    if (value.slice(0, prefix.length).toLocaleLowerCase() !== prefix.toLocaleLowerCase()) {
        return value;
    }
    return value.slice(prefix.length).replace(/^\s+/, "");
};

const collapseOverlappingPlainTextEdits = (tokens: CorrectedTextToken[]): CorrectedTextToken[] =>
    tokens.map((token, index): CorrectedTextToken => {
        if (token.type !== "replacement" && token.type !== "deletion") return token;

        const prev = tokens[index - 1];
        if (prev?.type !== "text") return token;

        if (!plainTextOverlapsWrong(prev.text, token.wrong)) return token;

        const wrong = stripOverlappingPrefix(prev.text, token.wrong);
        if (token.type === "deletion") {
            return { type: "deletion", wrong };
        }

        return {
            type: "replacement",
            wrong,
            correct: stripOverlappingPrefix(prev.text, token.correct),
        };
    });

const sanitizeCorrectedTextTokens = (tokens: CorrectedTextToken[]): CorrectedTextToken[] =>
    tokens.map((token, index): CorrectedTextToken => {
        if (token.type !== "replacement") return token;

        const previous = tokens[index - 1];
        const next = tokens[index + 1];
        const correctAlreadyBefore =
            previous?.type === "text" && endsWithStandaloneTerm(previous.text, token.correct);
        const correctAlreadyAfter =
            next?.type === "text" && startsWithStandaloneTerm(next.text, token.correct);
        const isNoOp = normalizeEditText(token.wrong) === normalizeEditText(token.correct);

        if (isNoOp || correctAlreadyBefore || correctAlreadyAfter || !token.correct.trim()) {
            return { type: "deletion", wrong: token.wrong };
        }

        return token;
    });

export const parseCorrectedTextTokens = (text: string): CorrectedTextToken[] => {
    const tokens: CorrectedTextToken[] = [];
    const normalizedText = text.replace(/\{\{([^{}]*)\}\}/g, "{$1}");
    let buffer = "";
    let i = 0;

    const flushBuffer = () => {
        if (!buffer) return;
        tokens.push({ type: "text", text: buffer });
        buffer = "";
    };

    while (i < normalizedText.length) {
        const char = normalizedText[i];

        if (char === "{") {
            flushBuffer();
            const end = normalizedText.indexOf("}", i);
            if (end === -1) {
                buffer += char;
                i += 1;
                continue;
            }

            const wrong = normalizedText.slice(i + 1, end);
            const next = end + 1;
            if (normalizedText[next] === "[") {
                const correctionEnd = normalizedText.indexOf("]", next);
                if (correctionEnd !== -1) {
                    const correct = normalizedText.slice(next + 1, correctionEnd);
                    if (wrong) {
                        tokens.push({ type: "replacement", wrong, correct });
                    } else if (correct) {
                        tokens.push({ type: "insertion", correct });
                    }
                    i = correctionEnd + 1;
                    continue;
                }
            }

            if (wrong) {
                tokens.push({ type: "deletion", wrong });
            }
            i = end + 1;
            continue;
        }

        if (char === "[") {
            flushBuffer();
            const end = normalizedText.indexOf("]", i);
            if (end === -1) {
                buffer += char;
                i += 1;
                continue;
            }
            const correct = normalizedText.slice(i + 1, end);
            if (correct) {
                tokens.push({ type: "insertion", correct });
            }
            i = end + 1;
            continue;
        }

        buffer += char;
        i += 1;
    }

    flushBuffer();
    return sanitizeCorrectedTextTokens(collapseOverlappingPlainTextEdits(tokens));
};
